<template>
  <div class="flex flex-col h-full min-h-0">
    <div class="flex-1 overflow-y-auto scrollbar-thin scrollbar-track-transparent">
      <!-- Staff room — one tenant-wide channel every admin + label manager
           is automatically a member of. Distinct from the directory below
           (which is a list of people to DM). Shown independently of the
           directory fetch so it's always available for staff. -->
      <button
        v-if="staffCanSee"
        type="button"
        class="w-full flex items-center gap-3 px-3 py-3 border-b border-white/5 hover:bg-primary/[0.07] transition-colors text-left bg-primary/[0.04] disabled:opacity-60 disabled:cursor-not-allowed"
        :disabled="staffEntering"
        @click="openStaffRoom"
      >
        <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-primary/25 to-purple-500/15 border border-primary/20 flex items-center justify-center shrink-0">
          <el-icon size="18" class="text-primary"><ChatLineRound /></el-icon>
        </div>
        <div class="flex-1 min-w-0">
          <div class="flex items-center justify-between gap-2">
            <span class="text-sm font-bold text-white truncate">{{ $t('chat.staff.channel_title', 'Staff Channel') }}</span>
            <span
              v-if="staffMeta.unread > 0"
              class="text-[10px] font-bold bg-primary text-black rounded-full px-1.5 py-0.5 leading-none shrink-0"
            >{{ staffMeta.unread > 99 ? '99+' : staffMeta.unread }}</span>
          </div>
          <p class="text-[11px] text-text-muted truncate">
            {{ $t('chat.staff.channel_sub', 'All admins & label managers') }}
          </p>
        </div>
      </button>

      <!-- Loading -->
      <div v-if="pending" class="flex justify-center py-8">
        <div class="w-6 h-6 border-2 border-white/10 border-t-primary rounded-full animate-spin"></div>
      </div>

      <template v-else>
        <!-- "My Roster" — scouts-only. The user accounts behind the
             artists this scout has active ARContracts on, so the scout
             can DM their managed artists directly (instead of digging
             through the tenant-wide user search). One row per user,
             annotated with the artist context. Empty (and rendered
             nowhere) for non-scout callers. -->
        <template v-if="data?.myRoster?.length">
          <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">
              {{ $t('chat.staff.my_roster', 'My Roster') }}
            </span>
            <span class="text-[10px] text-text-dim tabular-nums">{{ data.myRoster.length }}</span>
          </div>
          <button
            v-for="u in data.myRoster"
            :key="`roster-${u.id}`"
            type="button"
            class="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-secondary/[0.06] transition-colors text-left bg-secondary/[0.03]"
            :title="$t('chat.actions.open_dm')"
            :disabled="busyId === u.id"
            @click="openDM(u.id)"
          >
            <div class="relative shrink-0">
              <img
                :src="u.avatar || '/images/default-avatar.svg'"
                :alt="u.name"
                class="w-9 h-9 rounded-full object-cover ring-2 ring-secondary/50 ring-offset-2 ring-offset-transparent"
              />
              <span
                v-if="isOnline(u.id)"
                class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color)]"
                :class="dotClass(u.id)"
              ></span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm text-white font-semibold truncate">{{ u.name }}</p>
              <p class="text-[10px] text-secondary font-medium truncate">{{ u.artistName }}</p>
            </div>
            <el-icon size="14" class="text-secondary shrink-0"><ChatDotRound /></el-icon>
          </button>
        </template>

        <!-- "Your label managers" — highlighted (cyan accent border) for
             artists whose tracks are signed to one of the LM's labels.
             Hidden for accounts that don't own any artists. -->
        <template v-if="data?.myLabelManagers?.length">
          <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between">
            <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">
              {{ $t('chat.staff.my_lms') }}
            </span>
            <span class="text-[10px] text-text-dim tabular-nums">{{ data.myLabelManagers.length }}</span>
          </div>
          <button
            v-for="u in data.myLabelManagers"
            :key="`my-${u.id}`"
            type="button"
            class="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-primary/[0.06] transition-colors text-left bg-primary/[0.03]"
            :title="$t('chat.actions.open_dm')"
            :disabled="busyId === u.id"
            @click="openDM(u.id)"
          >
            <div class="relative shrink-0">
              <img
                :src="u.avatar || '/images/default-avatar.svg'"
                :alt="u.name"
                class="w-9 h-9 rounded-full object-cover ring-2 ring-primary/60 ring-offset-2 ring-offset-transparent"
              />
              <span
                v-if="isOnline(u.id)"
                class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color)]"
                :class="dotClass(u.id)"
              ></span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm text-white font-semibold truncate">{{ u.name }}</p>
              <p class="text-[10px] text-primary font-medium">{{ $t('chat.staff.lm_role') }}</p>
            </div>
            <el-icon size="14" class="text-primary shrink-0"><ChatDotRound /></el-icon>
          </button>
        </template>

        <!-- Admins -->
        <template v-if="data?.admins?.length">
          <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between mt-1">
            <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">
              {{ $t('chat.staff.admins') }}
            </span>
            <span class="text-[10px] text-text-dim tabular-nums">{{ data.admins.length }}</span>
          </div>
          <button
            v-for="u in data.admins"
            :key="`a-${u.id}`"
            type="button"
            class="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] transition-colors text-left"
            :title="$t('chat.actions.open_dm')"
            :disabled="busyId === u.id"
            @click="openDM(u.id)"
          >
            <div class="relative shrink-0">
              <img
                :src="u.avatar || '/images/default-avatar.svg'"
                :alt="u.name"
                class="w-9 h-9 rounded-full object-cover"
              />
              <span
                v-if="isOnline(u.id)"
                class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color)]"
                :class="dotClass(u.id)"
              ></span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm text-white font-semibold truncate">{{ u.name }}</p>
              <p class="text-[10px] text-violet-300 font-medium">{{ $t('chat.staff.admin_role') }}</p>
            </div>
            <el-icon size="14" class="text-text-muted shrink-0"><ChatDotRound /></el-icon>
          </button>
        </template>

        <!-- All other Label Managers -->
        <template v-if="data?.labelManagers?.length">
          <div class="px-3 py-2 border-b border-white/5 flex items-center justify-between mt-1">
            <span class="text-[10px] uppercase tracking-widest text-text-muted font-semibold">
              {{ $t('chat.staff.label_managers') }}
            </span>
            <span class="text-[10px] text-text-dim tabular-nums">{{ data.labelManagers.length }}</span>
          </div>
          <button
            v-for="u in data.labelManagers"
            :key="`lm-${u.id}`"
            type="button"
            class="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 hover:bg-white/[0.03] transition-colors text-left"
            :title="$t('chat.actions.open_dm')"
            :disabled="busyId === u.id"
            @click="openDM(u.id)"
          >
            <div class="relative shrink-0">
              <img
                :src="u.avatar || '/images/default-avatar.svg'"
                :alt="u.name"
                class="w-9 h-9 rounded-full object-cover"
              />
              <span
                v-if="isOnline(u.id)"
                class="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ring-2 ring-[var(--surface-color)]"
                :class="dotClass(u.id)"
              ></span>
            </div>
            <div class="min-w-0 flex-1">
              <p class="text-sm text-white font-semibold truncate">{{ u.name }}</p>
              <p class="text-[10px] text-text-muted font-medium">{{ $t('chat.staff.lm_role') }}</p>
            </div>
            <el-icon size="14" class="text-text-muted shrink-0"><ChatDotRound /></el-icon>
          </button>
        </template>

        <!-- Empty state — nothing surfaced at all -->
        <div
          v-if="!data?.myRoster?.length && !data?.myLabelManagers?.length && !data?.admins?.length && !data?.labelManagers?.length"
          class="text-center py-10 px-4"
        >
          <p class="text-sm text-text-muted">{{ $t('chat.staff.empty_title') }}</p>
          <p class="text-[11px] text-text-dim mt-1">{{ $t('chat.staff.empty_hint') }}</p>
        </div>

        <!-- Inline error -->
        <div
          v-if="error"
          class="mx-3 mt-3 px-3 py-2 rounded-md text-[11px] bg-rose-400/[0.08] border border-rose-400/25 text-rose-300"
        >
          {{ error }}
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ChatDotRound, ChatLineRound } from '@element-plus/icons-vue'
import { useChatUI } from '~/composables/chat/useChatUI'
import { useChatThreads } from '~/composables/chat/useChatThreads'
import { useChatStaffRoom } from '~/composables/chat/useChatStaffRoom'
import { usePresence } from '~/composables/chat/usePresence'

type StaffUser = { id: string; name: string; avatar: string | null; role: string }
type RosterUser = StaffUser & { artistName: string; artistId: string }
type Payload = {
  myRoster: RosterUser[]
  myLabelManagers: StaffUser[]
  admins: StaffUser[]
  labelManagers: StaffUser[]
}

// Fetched on mount + refresh every time the tab is shown — staff
// directory is small and changes infrequently, so a single fetch
// per panel-open is cheap. SSR-skipped because chat state never
// renders during SSR (panel mounts client-side only).
const { data, pending, error: fetchError, refresh } = await useFetch<Payload>('/api/chat/staff', {
  default: () => ({ myRoster: [], myLabelManagers: [], admins: [], labelManagers: [] }),
  server: false
})

// Re-fetch each time the staff tab becomes active so a freshly-promoted
// LM (or a just-revoked one) is reflected without a full page reload. The
// staff-room unread badge is refreshed on the same trigger.
const { activeTab, setTab, openDMThread } = useChatUI()

// Staff room (the channel). Membership is role-derived server-side; `canSee`
// only decides whether we render the entry. The unread badge is hydrated via
// loadRoom() (a GET that does NOT mark read).
const {
  meta: staffMeta,
  canSee: staffCanSee,
  entering: staffEntering,
  loadRoom: loadStaffRoom,
  enterStaff,
  markStaffRead
} = useChatStaffRoom()

// Initial badge load. NOT onMounted: this component's setup has a top-level
// `await useFetch` above, and a lifecycle hook registered after an await no
// longer has an active instance (silently never fires). A direct, client-only
// call is safe here — the chat panel only ever mounts client-side.
if (import.meta.client && staffCanSee.value) loadStaffRoom()
watch(activeTab, (t) => {
  if (t === 'staff') {
    refresh()
    if (staffCanSee.value) loadStaffRoom()
  }
})

const { startDM } = useChatThreads()
const { isOnline, getStatus } = usePresence()
// Amber for AFK, red for do-not-disturb — the dots were always green.
function dotClass(userId: string) {
  const s = getStatus(userId)
  return s === 'afk' ? 'bg-amber-400' : s === 'dnd' ? 'bg-red-500' : 'bg-emerald-400'
}

const busyId = ref<string | null>(null)
const error = ref<string | null>(null)

async function openStaffRoom() {
  if (staffEntering.value) return
  error.value = null
  try {
    const threadId = await enterStaff()
    if (!threadId) throw new Error('failed')
    markStaffRead()
    openDMThread(threadId)
    setTab('dms')
  } catch {
    error.value = 'Could not open the staff channel. Please try again.'
  }
}

async function openDM(userId: string) {
  if (busyId.value) return
  busyId.value = userId
  error.value = null
  try {
    const threadId = await startDM(userId)
    if (!threadId) throw new Error('failed')
    openDMThread(threadId)
    setTab('dms')
  } catch {
    error.value = 'Could not open conversation. Please try again.'
  } finally {
    busyId.value = null
  }
}

// Surface fetch errors too (e.g. tenant has chat disabled → 404).
watch(fetchError, (e) => {
  if (e) error.value = (e as any)?.statusMessage || 'Failed to load staff list.'
})
</script>
